import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { ButtonStyleProps } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.js';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.js';
import {
  RadioGroup,
  RadioGroupProps,
} from '@sola/ark-core/molecules/radio-group/radio-group.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { PaymentTypeEnum } from '@sola/schema/dotcom/proxy-api/paymentMethods.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { usePaymentMethodRemove } from '@sola/supporter-sdk/hooks.js';

import { CommitmentAvatarGroup } from '../../molecules/commitment-avatar-group/commitment-avatar-group';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider';
import {
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getErrorTranslationKeys } from '../../utils/errors';

const PaymentMethodLabel = ({
  paymentMethod,
}: {
  paymentMethod: GraphQLSupporterPaymentMethod;
}) => {
  const { accountNickname, paymentType, creditCard, ach } = paymentMethod;
  return (
    <IntlBlock
      i18nKey={
        accountNickname
          ? 'my:deletePaymentMethod.paymentMethodLabel.withNickname'
          : 'my:deletePaymentMethod.paymentMethodLabel.default'
      }
      values={{
        nickname: accountNickname,
        label:
          paymentType === PaymentTypeEnum.CreditCard
            ? creditCard?.cardType
            : 'Checking',
        lastFour:
          paymentType === PaymentTypeEnum.CreditCard
            ? creditCard?.lastFour
            : ach?.lastFourOfAccountNumber,
      }}
    />
  );
};

const PaymentMethodSupportingText = ({
  paymentMethod,
}: {
  paymentMethod: GraphQLSupporterPaymentMethod;
}) => {
  return (
    <IntlBlock
      i18nKey="my:deletePaymentMethod.removeMethodDialog.supportingTextRecurring"
      components={{
        PaymentLabel: <PaymentMethodLabel paymentMethod={paymentMethod} />,
      }}
    />
  );
};

const mapPaymentMethodsToRadioGroupItem = (
  paymentMethods?: GraphQLSupporterPaymentMethod[],
): RadioGroupProps['items'] => {
  if (!paymentMethods?.length) {
    return undefined;
  }

  return paymentMethods
    .map((paymentMethod) => {
      if (
        !paymentMethod.id ||
        (!paymentMethod.ach && !paymentMethod.creditCard)
      ) {
        return null;
      }

      return {
        value: paymentMethod.id,
        element: (
          <span className="flex items-center gap-comfy-xs">
            <PaymentMethodLabel paymentMethod={paymentMethod} />
            {paymentMethod.accountNickname && (
              <Badge label={paymentMethod.accountNickname} size="sm" />
            )}
          </span>
        ),
      };
    })
    .filter((item) => !!item);
};

export type PaymentMethodRemoveDialogProps = Omit<DialogProps, 'modal'> & {
  openTrigger: React.ReactElement<ButtonStyleProps>;
  recurringCommitments: GraphQLCommitment[];
  selectedPaymentMethod: GraphQLSupporterPaymentMethod;
  alternatePaymentMethods: GraphQLSupporterPaymentMethod[];
};

export const PaymentMethodRemoveDialog: React.FC<
  PaymentMethodRemoveDialogProps
> = ({
  openTrigger,
  selectedPaymentMethod,
  recurringCommitments,
  alternatePaymentMethods,
}) => {
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [newPaymentMethodId, setNewPaymentMethodId] = useState<
    GraphQLSupporterPaymentMethod['id'] | null
  >(null);
  const { mutateAsync, isPending } = usePaymentMethodRemove(
    selectedPaymentMethod?.id || undefined,
  );
  const { addAlerts, clearAlerts } = useLocalAlerts();

  const { t } = useTranslation('my');

  const handleRemove = async () => {
    if (!selectedPaymentMethod.id) {
      return;
    }

    clearAlerts();
    try {
      if (recurringCommitments.length && newPaymentMethodId) {
        console.log('TODO: MOVING COMMITMENTS TO NEW PAYMENT METHOD');
        // await Promise.all([
        // //  TODO: Make the API call to the server-side - that should handle the moving
        // ])
      }

      await mutateAsync();
      addAlerts(
        createPaymentMethodSuccessAlert(
          selectedPaymentMethod,
          'deletePaymentMethod.success.action',
        ),
      );
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodDelete,
        errors,
      );
      addAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }

    // TODO: Make sure dialog can close when form submits
    setIsRemoveDialogOpen(false);
  };

  return (
    <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        size="sm"
        icon="info-circle"
        purpose="negative"
        title={t('deletePaymentMethod.removeMethodDialog.title')}
        supportingText={
          recurringCommitments.length ? (
            <PaymentMethodSupportingText
              paymentMethod={selectedPaymentMethod}
            />
          ) : (
            t('deletePaymentMethod.removeMethodDialog.supportingText')
          )
        }
      >
        {!!recurringCommitments.length && (
          <>
            <div
              className={cn([
                'body-neutral-mild',
                'pl-snug-md', // NOTE: provides space for radio focus box shadow
              ])}
            >
              <RadioGroup
                name="payment-method-select"
                loop={false}
                value={newPaymentMethodId}
                onChange={(e) => {
                  setNewPaymentMethodId(e.target.value);
                }}
                items={mapPaymentMethodsToRadioGroupItem(
                  alternatePaymentMethods,
                )}
              />
            </div>
            <Divider purpose="neutral" className="my-comfy-xl" />
            <div className="flex flex-col gap-comfy-sm">
              <p className="body-neutral-mild">
                <IntlBlock i18nKey="my:deletePaymentMethod.removeMethodDialog.newPaymentMethodNote" />
              </p>
              <CommitmentAvatarGroup
                commitments={recurringCommitments}
                itemsToShow={3}
                layout="stacked"
                tooltipMessage={t('payments.methodCommitmentsTooltip', {
                  count: recurringCommitments.length,
                })}
                usePortal={false}
              />
            </div>
          </>
        )}
        <DialogActions>
          {recurringCommitments.length ? (
            <>
              <DialogActionClose>
                {t('deletePaymentMethod.removeMethodDialog.cancel')}
              </DialogActionClose>
              <DialogActionPrimary
                purpose="negative"
                isBusy={isPending}
                disabled={!newPaymentMethodId}
                onClick={handleRemove}
              >
                {t('deletePaymentMethod.removeMethodDialog.saveAndRemove')}
              </DialogActionPrimary>
            </>
          ) : (
            <>
              <DialogActionClose>
                {t('deletePaymentMethod.removeMethodDialog.cancel')}
              </DialogActionClose>
              <DialogActionPrimary
                purpose="negative"
                isBusy={isPending}
                onClick={handleRemove}
              >
                {t('deletePaymentMethod.removeMethodDialog.remove')}
              </DialogActionPrimary>
            </>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
